// =======================================
// SCAREditor.jsx — 알림 기능 포함 완전 버전
// =======================================

import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import TableCell from "../../common/ui/TableCell";
import ReferenceModal from "../../approval/ReferenceModal";
import PhotoBoardSelect from "../../const/Photo/PhotoBoardSelect";
import PhotoBoardReadonly from "../../const/Photo/PhotoBoardReadonly";


export default function SCAREditor({
  initial = {},
  onChange,
  readOnly = false,
}) {
  const editorRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const meId = me?.id || null;

  /* ================================
     기본 상태
  ================================ */
  const STATUS_BY_TYPE = [
    { value: "action_needed", label: "조치 필요" },
    { value: "in_progress", label: "조치 중" },
    { value: "urgent", label: "긴급" },
    { value: "cancelled", label: "취소" },
  ];

  const [title, setTitle] = useState(initial.title || "");
  const [progress, setProgress] = useState(initial.progress || "action_needed");
  const author = (initial.author || me?.name || "").trim();
  const docDate = String(initial.doc_date || today).slice(0, 10);
  const [instructions, setInstructions] = useState(
    initial.meta?.instructions || initial.instructions || ""
  );

  const [instructionText, setInstructionText] = useState(
    initial.meta?.instructions || initial.instructions || ""
  );

  const [instructionPhoto, setInstructionPhoto] = useState(
    initial.meta?.instruction_photoboard || null
  );

  const [openPhotoModal, setOpenPhotoModal] = useState(false);


  /* 상세 내용 */
  const [scarProblem, setScarProblem] = useState(initial.scarProblem || "");
  const [scarImmediate, setScarImmediate] = useState(
    initial.scarImmediate || ""
  );
  const [scarRootCause, setScarRootCause] = useState(
    initial.scarRootCause || ""
  );
  const [showDetailTable, setShowDetailTable] = useState(false);

  /* ================================
      담당자(참조자)
  ================================ */
  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [referenceUserDetails, setReferenceUserDetails] = useState([]);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

  const openReferenceModal = () => setIsReferenceModalOpen(true);
  const closeReferenceModal = () => setIsReferenceModalOpen(false);

  const handleRefChange = (ids) => setReferenceUserIds(ids);
  const handleRefSave = (ids, details) => {
    setReferenceUserIds(ids);
    setReferenceUserDetails(details || []);
    return true;
  };

  /* ================================
      🔥 알림 / 긴급 알림 (원본 유지)
  ================================ */
  const [sendAlert, setSendAlert] = useState(
    initial.sendAlert ?? initial.meta?.send_alert ?? false
  );
  const [alertUrgent, setAlertUrgent] = useState(
    initial.alertUrgent ?? initial.meta?.alert_urgent ?? false
  );

  /* ================================
      자동 제목
  ================================ */
  const autoTitle = useMemo(() => {
    const label =
      STATUS_BY_TYPE.find((s) => s.value === progress)?.label || "조치 필요";
    return `[SCAR] ${label} - ${today}`;
  }, [progress]);

  /* ================================
      부모(FormContentManager)로 meta 전달
  ================================ */
  useEffect(() => {
    const html =
      editorRef.current?.getContent?.({ format: "html" }) || instructions;

    onChange?.({
      dns: initial.dns || "",
      title: (title ?? "").trim() || autoTitle,
      author,
      doc_type: "scar",
      status: "none",

      meta: {
        doc_date: docDate,
        progress,
        instructions: html,

        instructions: instructionText,
        instruction_photoboard: instructionPhoto,

        scarProblem,
        scarImmediate,
        scarRootCause,

        reference_users: [...referenceUserIds, meId],
        referenceUserDetails,

        send_alert: sendAlert,
        alert_urgent: alertUrgent,
      },
    });
  }, [
    title,
    progress,
    docDate,
    instructions,
    scarProblem,
    scarImmediate,
    scarRootCause,
    referenceUserIds,
    sendAlert,
    alertUrgent,
    instructionText,
    instructionPhoto,
  ]);

  const section = "";

  /* ================================
      상세 표 토글
  ================================ */
  const toggleDetailTable = () => {
    if (showDetailTable) {
      const hasData =
        scarProblem.trim() || scarImmediate.trim() || scarRootCause.trim();

      if (hasData) {
        const ok = window.confirm(
          "입력된 상세 내용이 모두 삭제됩니다.\n계속하시겠습니까?"
        );
        if (!ok) return;
      }

      setScarProblem("");
      setScarImmediate("");
      setScarRootCause("");
    }

    setShowDetailTable((v) => !v);
  };

  /* ======================================================================
      RENDER
  ====================================================================== */
  return (
    <div className="w-full space-y-6">
      {/* ===========================
          1) 문서 기본 정보
      =========================== */}
      <section className={section}>
        {/* <div className="text-body-bold mb-2">문서 정보</div> */}

        <table className="w-full text-sm border">
          <tbody>
            {/* 제목 */}
            <TableCell
              label="제목"
              colSpan={3}
              editing={!readOnly}
              value={title || autoTitle}
              input={
                <>
                  <input
                    className="input-base w-full"
                    value={title}
                    placeholder={autoTitle}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    미입력 시 자동 제목: {autoTitle}
                  </p>
                </>
              }
            />

            {/* 문서 유형 + 상태 */}
            <tr>
              <TableCell label="문서 유형" value="SCAR" />

              <TableCell
                label="상태"
                editing={!readOnly}
                value={
                  STATUS_BY_TYPE.find((s) => s.value === progress)?.label || "-"
                }
                input={
                  <select
                    className="input-base"
                    value={progress}
                    onChange={(e) => setProgress(e.target.value)}
                  >
                    {STATUS_BY_TYPE.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                }
              />
            </tr>

            {/* 작성자 + 날짜 */}
            <tr>
              <TableCell label="작성자" value={author || "-"} />
              <TableCell label="작성일" value={docDate || "-"} />
            </tr>

            {/* SCAR 본문 */}
            <TableCell
              label="부적합 내용"
              colSpan={3}
              editing={!readOnly}
              value={instructionText}
              input={
                <div className="space-y-3">
                  <textarea
                    className="input-base w-full min-h-[220px]"
                    placeholder="부적합 내용을 입력하세요"
                    value={instructionText}
                    onChange={(e) => setInstructionText(e.target.value)}
                  />

                  {/* 📸 사진대지 */}
                  {instructionPhoto ? (
                    <div>
                      <PhotoBoardReadonly photoboardId={instructionPhoto} />
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn-basic-sm mt-2"
                          onClick={() => setOpenPhotoModal(true)}
                        >
                          사진대지 변경
                        </button>
                      )}
                    </div>
                  ) : (
                    !readOnly && (
                      <button
                        type="button"
                        className="btn-basic-sm"
                        onClick={() => setOpenPhotoModal(true)}
                      >
                        사진대지 선택
                      </button>
                    )
                  )}
                </div>
              }
            />

          </tbody>
        </table>

        <div className="mt-3">
          <button className="btn-basic-sm" onClick={toggleDetailTable}>
            {showDetailTable ? "상세 표 닫기" : "상세 내용 표로 입력"}
          </button>
        </div>

        {showDetailTable && (
          <table className="w-full text-sm border mt-3">
            <tbody>
              <TableCell
                label="안전 위반 사항"
                editing={!readOnly}
                value={scarProblem}
                input={
                  <textarea
                    className="input-base w-full h-24"
                    value={scarProblem}
                    onChange={(e) => setScarProblem(e.target.value)}
                  />
                }
              />

              <TableCell
                label="즉시 조치 사항"
                editing={!readOnly}
                value={scarImmediate}
                input={
                  <textarea
                    className="input-base w-full h-24"
                    value={scarImmediate}
                    onChange={(e) => setScarImmediate(e.target.value)}
                  />
                }
              />

              <TableCell
                label="발생 원인"
                editing={!readOnly}
                value={scarRootCause}
                input={
                  <textarea
                    className="input-base w-full h-24"
                    value={scarRootCause}
                    onChange={(e) => setScarRootCause(e.target.value)}
                  />
                }
              />
            </tbody>
          </table>
        )}
      </section>

      {/* ===========================
          2) 담당자 + 알림 설정
      =========================== */}
      <section className={section}>
        <div className="text-body-bold mb-2">담당자 지정</div>

        {!readOnly && (
          <>
            <button className="btn-basic-sm" onClick={openReferenceModal}>
              담당자 선택
            </button>

            {/* 선택된 담당자 */}
            {referenceUserDetails.length > 0 && (
              <div className="mt-2 space-y-1 text-sm">
                {referenceUserDetails.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 border border-gray-300 mr-1"
                  >
                    {u.last_name}
                    {u.first_name}
                    {u.position && (
                      <span className="text-xs text-gray-500">
                        {u.position}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">({u.company})</span>
                  </span>
                ))}
              </div>
            )}

            {/* 🔥 알림 보내기 */}
            <div className="mt-3 space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-blue-600"
                  checked={sendAlert}
                  onChange={(e) => setSendAlert(e.target.checked)}
                />
                <span>담당자에게 알림 보내기</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-red-600"
                  checked={alertUrgent}
                  disabled={!sendAlert}
                  onChange={(e) => setAlertUrgent(e.target.checked)}
                />
                <span className={!sendAlert ? "text-gray-400" : ""}>
                  긴급 알림
                </span>
              </label>
            </div>
          </>
        )}
      </section>

      {/* 담당자 모달 */}
      <ReferenceModal
        isOpen={isReferenceModalOpen}
        onClose={closeReferenceModal}
        selectedIds={referenceUserIds}
        onChange={handleRefChange}
        onSave={handleRefSave}
        name="담당자"
      />
      <PhotoBoardSelect
        isOpen={openPhotoModal}
        onClose={() => setOpenPhotoModal(false)}
        onSelect={(board) => {
          setInstructionPhoto(board.id);
          setOpenPhotoModal(false);
        }}
      />

    </div>

  );
}
